import { useEffect } from 'react'

import * as ScrollArea from '@radix-ui/react-scroll-area'
import cn from 'clsx'
import type { LoaderFunctionArgs, HeadersFunction } from 'react-router'
import { Link, NavLink, Outlet, useLoaderData } from 'react-router'

import MiniSiteFooter from '@/components/MiniSiteFooter'
import SiteHeader from '@/components/SiteHeader'
import { cacheControl } from '@/http.server'
import { findAllChapters, findCourse } from '@/models/course.server'

type LoaderData = typeof loader

export async function loader({ params }: LoaderFunctionArgs) {
  const course = findCourse(params.course as string)

  if (!course) {
    throw new Response('Not Found', { status: 404 })
  }

  const chapters = findAllChapters(params.course as string)

  return {
    course: course,
    chapters: chapters
  }
}

export const headers: HeadersFunction = () => ({
  'Cache-Control': cacheControl
})

export default function Course() {
  const { course, chapters } = useLoaderData<LoaderData>()

  useEffect(() => {
    scrollActiveNavLinkIntoView()
  }, [])

  return (
    <div className='flex min-h-screen flex-col justify-between'>
      <SiteHeader isFullWidth />

      <header className='flex flex-col items-center justify-center space-y-3 px-1 pb-4 md:px-5 lg:flex-row lg:justify-between lg:gap-3 lg:space-y-0'>
        <Link className='flex-auto shrink-0 font-semibold text-primary' to={course.path}>
          {course.name}
        </Link>

        <ScrollArea.Root className='w-full md:w-auto'>
          <ScrollArea.Viewport className='mb-2 lg:mb-0'>
            <nav className='relative flex flex-nowrap gap-x-1 gap-y-1 text-primary sm:justify-center md:justify-start md:gap-y-2'>
              {chapters.map((chapter) => (
                <NavLink
                  className={({ isActive }) =>
                    cn(
                      'group inline-flex items-center justify-center gap-1 px-1.5 py-1 font-medium whitespace-nowrap text-primary transition hover:bg-secondary hover:text-primary',
                      {
                        'bg-secondary': isActive
                      }
                    )
                  }
                  key={chapter.path}
                  onClick={(event) => {
                    event.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
                  }}
                  prefetch='intent'
                  to={chapter.path}
                >
                  {({ isActive }) => (
                    <span
                      className={cn({
                        'text-secondary hover:text-primary': !isActive,
                        'text-primary': isActive
                      })}
                    >
                      <span
                        aria-hidden='true'
                        className={cn('hidden lg:inline', {
                          'text-orange': isActive,
                          'text-secondary': !isActive
                        })}
                      >
                        {chapter.sequence}.&nbsp;
                      </span>

                      {chapter.title}
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>
          </ScrollArea.Viewport>
        </ScrollArea.Root>
      </header>

      <Outlet />

      <MiniSiteFooter />
    </div>
  )

  /**
   * Make sure the active nav link is always visible on small screens.
   */

  function scrollActiveNavLinkIntoView() {
    const activeNavLink = document.querySelector<HTMLAnchorElement>('[aria-current="page"]')

    if (activeNavLink) {
      activeNavLink.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'center' })
    }
  }
}
